/**
 * Legacy Import Mixin
 *
 * Provides Alpine.js state and methods for importing legacy ledger entries
 * via CSV upload. Spread into any account slideover component.
 *
 * Usage:
 *   import { legacyImportMixin } from './legacy_import_mixin'
 *   return { ...legacyImportMixin('tenancy'), ...otherState }
 */
export function legacyImportMixin(accountableType) {
  return {
    // State
    showLegacyImport: false,
    legacyImporting: false,
    legacyImportResult: null,
    legacyImportDragOver: false,
    legacyOpeningBalanceConfirm: false,
    legacyPendingBalancePence: 0,
    legacyConfirming: false,

    openLegacyImport() {
      this.showLegacyImport = true
      this.legacyImportResult = null
      this.legacyOpeningBalanceConfirm = false
    },

    cancelLegacyImport() {
      this.showLegacyImport = false
      this.legacyImporting = false
      this.legacyImportResult = null
      this.legacyOpeningBalanceConfirm = false
      this.legacyPendingBalancePence = 0
      this.legacyConfirming = false
    },

    handleLegacyFileDrop(event) {
      const file = event.dataTransfer?.files?.[0]
      if (file) this.uploadLegacyFile(file)
    },

    handleLegacyFileSelect(event) {
      const file = event.target?.files?.[0]
      if (file) this.uploadLegacyFile(file)
      // Reset input so the same file can be re-selected
      event.target.value = ''
    },

    async uploadLegacyFile(file) {
      const name = file.name.toLowerCase()
      if (!name.endsWith('.csv') && !name.endsWith('.xlsx') && !name.endsWith('.xls')) {
        this.legacyImportResult = { success: false, error: 'Please upload a CSV or Excel (.xlsx) file' }
        return
      }

      this.legacyImporting = true
      this.legacyImportResult = null
      this.legacyOpeningBalanceConfirm = false

      const accountableId = this._legacyAccountableId()

      const formData = new FormData()
      formData.append('file', file)
      formData.append('accountable_type', accountableType)
      formData.append('accountable_id', accountableId)

      try {
        const response = await fetch('/accounting/accounts/legacy_imports', {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: formData
        })

        if (!response.ok) {
          let errorMessage = `Server error (${response.status})`
          try {
            const errorData = await response.json()
            if (errorData.error) errorMessage = errorData.error
          } catch (_) {
            // Response wasn't JSON (e.g. 500 HTML page) — use the status-based message
          }
          this.legacyImportResult = { success: false, error: errorMessage }
          this.legacyImporting = false
          return
        }

        const data = await response.json()
        this.legacyImportResult = data
        this.legacyImporting = false

        // Refresh the parent slideover data to show imported entries
        if (data.success && data.imported > 0) {
          this.refreshData()
        }

        // If there's a pending opening balance, show the confirmation step
        if (data.success && data.pending_opening_balance_pence && data.pending_opening_balance_pence !== 0) {
          this.legacyPendingBalancePence = data.pending_opening_balance_pence
          this.legacyOpeningBalanceConfirm = true
        } else if (data.success) {
          // No balance to confirm — auto-dismiss after 2 seconds
          setTimeout(() => { this.legacyImportResult = null }, 2000)
        }
      } catch (error) {
        const message = !navigator.onLine
          ? 'You appear to be offline — check your connection and try again'
          : 'Could not reach the server — please try again'
        this.legacyImportResult = { success: false, error: message }
        this.legacyImporting = false
      }
    },

    async confirmOpeningBalance(keep) {
      if (!keep) {
        // User chose "No" — balance doesn't represent real money. Just dismiss.
        this.legacyOpeningBalanceConfirm = false
        this.legacyPendingBalancePence = 0
        this.legacyImportResult = null
        return
      }

      // User chose "Yes" — create the opening balance
      this.legacyConfirming = true

      const accountableId = this._legacyAccountableId()

      try {
        const response = await fetch('/accounting/accounts/legacy_imports/confirm_opening_balance', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            accountable_type: accountableType,
            accountable_id: accountableId
          })
        })

        if (!response.ok) {
          let errorMessage = `Server error (${response.status})`
          try {
            const errorData = await response.json()
            if (errorData.error) errorMessage = errorData.error
          } catch (_) {}
          this.legacyImportResult = { success: false, error: errorMessage }
          this.legacyOpeningBalanceConfirm = false
          this.legacyConfirming = false
          return
        }

        const data = await response.json()
        this.legacyOpeningBalanceConfirm = false
        this.legacyConfirming = false
        this.legacyPendingBalancePence = 0

        if (data.success) {
          this.refreshData()
          this.legacyImportResult = null
        }
      } catch (error) {
        this.legacyConfirming = false
        const message = !navigator.onLine
          ? 'You appear to be offline — check your connection and try again'
          : 'Could not reach the server — please try again'
        this.legacyImportResult = { success: false, error: message }
        this.legacyOpeningBalanceConfirm = false
      }
    },

    // Override per slideover to return the correct ID
    _legacyAccountableId() {
      if (accountableType === 'tenancy') return this.tenancyId
      if (accountableType === 'landlord') return this.landlordId
      if (accountableType === 'contractor') return this.contractorId
      return null
    }
  }
}
